import {
  type AgEventType,
  type CellEvent,
  type GridApi,
  isColumn as isAgColumn,
  type ValueService,
} from 'ag-grid-community';
import { AGGRID_AUTOCOLUMN_ID } from '../BlotterTable/types';

type AgGridColumn = Parameters<ValueService['getValue']>[0];
type AgGridRowNode = Parameters<ValueService['getValue']>[1];

interface AgGridPrivateBeans {
  filterValueSvc?: {
    getValue: (column: AgGridColumn, rowNode: NonNullable<AgGridRowNode>) => ReturnType<ValueService['getValue']>;
  };
  valueSvc?: ValueService;
}

interface AgGridGetCellValueParams<TData, TValue> {
  api: GridApi<TData>;
  column: CellEvent<AgEventType, TData, TValue>['column'] | null;
  node?: CellEvent<AgEventType, TData, TValue>['node'] | null;
  value: CellEvent<AgEventType, TData, TValue>['value'];
}

type AgGridRowNodeWithPrivateBeans = NonNullable<AgGridGetCellValueParams<unknown, unknown>['node']> & {
  beans?: AgGridPrivateBeans;
};

function getAgGridPrivateBeans<TData, TValue>(
  node: AgGridGetCellValueParams<TData, TValue>['node']
): AgGridPrivateBeans | undefined {
  return (node as AgGridRowNodeWithPrivateBeans | null | undefined)?.beans;
}

/**
 * This function provides a method to get the "display value" of an ag-grid cell, using the same method
 * that ag-grid uses internally.
 *
 * @param params AgGrid params object (e.g. from an event callback, etc.)
 * @returns Value as formatted for display in an ag-grid cell (e.g. using `valueFormatter`, etc.)
 */
export function getCellDisplayValue<TData = any, TValue = any>(params: AgGridGetCellValueParams<TData, TValue>) {
  const invokedForRowGroupColumn = params.column?.getColId() === AGGRID_AUTOCOLUMN_ID;
  // If we are invoking on behalf of the row group column, we can either be a group row or a leaf row. For group rows, there'll be a rowGroupColumn.
  // Otherwise, params.column will contain the column relevant for the invoked cell this function is invoked for.
  const column = invokedForRowGroupColumn ? (params.node?.rowGroupColumn ?? params.column) : params.column;

  if (!column) {
    return params.value;
  }

  if (!params.node) {
    return params.value;
  }

  try {
    if (!isAgColumn(column)) {
      return params.value;
    }

    // We're grabbing this private property because we want to call formatValue the same way AG Grid does internally.
    const valueService = getAgGridPrivateBeans(params.node)?.valueSvc;
    if (!valueService) {
      return params.value;
    }
    const formattedValue = valueService.formatValue(column, params.node, params.value);

    return formattedValue ?? params.value;
  } catch {
    return params.value;
  }
}

/**
 * This function provides a method to get the "filter value" of an ag-grid cell, using the same method
 * that ag-grid uses internally.
 *
 * @param params AgGrid params object (e.g. from an event callback, etc.)
 * @returns Value that ag-grid will use when filtering this cell (e.g. using `filterValueGetter`, etc.)
 */
export function getCellFilterValue<TData = any, TValue = any>(params: AgGridGetCellValueParams<TData, TValue>) {
  if (!params.column) {
    return params.value;
  }

  if (!params.node) {
    return params.value;
  }

  try {
    if (!isAgColumn(params.column)) {
      return params.value;
    }

    // We're grabbing this private property because we want to call filterValueSvc the same way AG Grid does internally.
    const filterValueService = getAgGridPrivateBeans(params.node)?.filterValueSvc;
    if (!filterValueService) {
      return params.value;
    }

    const filterValue = filterValueService.getValue(params.column, params.node);
    return filterValue;
  } catch {
    return params.value;
  }
}
